import { isDefined } from "@clipboard-health/util-ts";

interface GetScoreSubtitleParams {
  wasLate: boolean;
  latenessMinutes?: number;
  didLosePointsForTimeliness: boolean;
  isArrivalTimeFeatureEnabled: boolean;
  isSecondShiftInDouble: boolean;
  wasBookedWithShortLeadTime: boolean;
  shortLeadTimeThresholdInHours: number;
  formattedTargetArrivalTime?: string;
}

function formatLateness(latenessMinutes?: number): string {
  return isDefined(latenessMinutes) ? `${latenessMinutes} minutes late` : "late";
}

/**
 * Determines the subtitle text for the EC clipboard score page
 * based on lateness, timeliness penalty, target arrival time, and second-in-double status.
 */
export function getScoreSubtitle(params: GetScoreSubtitleParams): string {
  const {
    wasLate,
    latenessMinutes,
    didLosePointsForTimeliness,
    isArrivalTimeFeatureEnabled,
    isSecondShiftInDouble,
    wasBookedWithShortLeadTime,
    shortLeadTimeThresholdInHours,
    formattedTargetArrivalTime,
  } = params;

  if (isSecondShiftInDouble) {
    return "On-time bonuses and late penalties don't apply to the second shift in a double.";
  }

  if (!wasLate) {
    return "You arrived on time! That helped your team and earned you bonus points.";
  }

  if (wasBookedWithShortLeadTime && !didLosePointsForTimeliness) {
    return `Since this shift was booked within ${Math.round(shortLeadTimeThresholdInHours)} hours of the start time, you didn't lose points for arriving late.`;
  }

  const hasTargetArrivalTime = isArrivalTimeFeatureEnabled && isDefined(formattedTargetArrivalTime);

  const lateness = formatLateness(latenessMinutes);

  if (didLosePointsForTimeliness) {
    if (hasTargetArrivalTime) {
      return `You committed to arriving by ${formattedTargetArrivalTime} at booking, but you arrived ${lateness}.`;
    }

    return `You arrived ${lateness} to this shift.`;
  }

  if (hasTargetArrivalTime) {
    return `You committed to arriving by ${formattedTargetArrivalTime}, but you were ${lateness}. You didn't lose points, but you missed the on-time bonus!`;
  }

  return `You were ${lateness}. You didn't lose points, but you missed the on-time bonus!`;
}
